import type { ReactElement, ReactNode } from 'react';
import { Link } from '@/components/link';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';

const focusRingClassName = focusVisibleOutline;

const chevronControlClassName = cn(
    'inline-flex size-6 shrink-0 items-center justify-center text-neutral-10',
    focusRingClassName,
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50'
);

const pageTileClassName = cn(
    'inline-flex size-10 shrink-0 items-center justify-center rounded-sm body-copy-m transition-colors',
    focusRingClassName
);

const pageTileActiveClassName = 'cursor-default bg-neutral-10 text-neutral-white';
const pageTileLinkClassName =
    'cursor-pointer bg-neutral-white text-neutral-10 hover:bg-neutral-40 hover:text-neutral-white';

const paginationEllipsisClassName = cn(
    pageTileClassName,
    'pointer-events-none select-none bg-neutral-white text-neutral-10'
);

type PaginationNavControlProps = {
    to: string;
    disabled?: boolean;
    className?: string;
    'aria-label': string;
};

export type PaginationPageButtonProps = PaginationNavControlProps & {
    isActive: boolean;
    children: ReactNode;
    'aria-current'?: 'page' | undefined;
};

/**
 * 40×40 page number tile (Figma node 267:91823 "tag toggle").
 * Current page: non-link `<button>`. Other pages: crawlable `<a>` with client-side pagination on click.
 */
export function PaginationPageButton({
    isActive,
    to,
    disabled,
    className,
    children,
    'aria-label': ariaLabel,
    'aria-current': ariaCurrent,
}: PaginationPageButtonProps): ReactElement {
    const tileClassName = cn(pageTileClassName, isActive ? pageTileActiveClassName : pageTileLinkClassName, className);

    if (disabled ?? isActive) {
        return (
            <button type="button" disabled className={tileClassName} aria-label={ariaLabel} aria-current={ariaCurrent}>
                {children}
            </button>
        );
    }

    return (
        <Link to={to} className={tileClassName} aria-label={ariaLabel}>
            {children}
        </Link>
    );
}

export type PaginationChevronButtonProps = PaginationNavControlProps & {
    children: ReactNode;
};

/**
 * 24×24 chevron control (Figma "chevron" frame).
 * Disabled at range bounds; otherwise crawlable `<a>` with client-side navigation on click.
 */
export function PaginationChevronButton({
    to,
    disabled,
    className,
    children,
    'aria-label': ariaLabel,
}: PaginationChevronButtonProps): ReactElement {
    const controlClassName = cn(chevronControlClassName, className);

    if (disabled) {
        return (
            <button type="button" disabled className={controlClassName} aria-label={ariaLabel}>
                {children}
            </button>
        );
    }

    return (
        <Link to={to} className={controlClassName} aria-label={ariaLabel}>
            {children}
        </Link>
    );
}

export type PaginationEllipsisProps = {
    className?: string;
};

export function PaginationEllipsis({ className }: PaginationEllipsisProps): ReactElement {
    return (
        <span className={cn(paginationEllipsisClassName, className)} aria-hidden>
            ...
        </span>
    );
}
